<template>
  <Avatar
    class="source-badge"
    :class="`source-badge--${source}`"
    :label="letter"
    :title="title"
    :style="dimensions"
  />
</template>

<script setup>
import { computed } from 'vue';
import Avatar from 'primevue/avatar';

// Where a bid came from, as a lettered square: F on the floor, O online, P a
// prebid. The three colors are domain signal — a clerk reads the history by them
// — so each is its own token rather than a shared accent.
defineOptions({ name: 'SourceBadge' });

const props = defineProps({
  // floor | online | prebid
  source: {
    type: String,
    default: 'floor',
    validator: (value) => ['floor', 'online', 'prebid'].includes(value),
  },
  // Edge length in pixels. The history list and the bid card size it differently.
  size: { type: Number, default: 24 },
});

const SOURCES = {
  floor: { letter: 'F', title: 'Floor bid' },
  online: { letter: 'O', title: 'Online bid' },
  prebid: { letter: 'P', title: 'Prebid' },
};
const meta = computed(() => SOURCES[props.source] ?? SOURCES.floor);
const letter = computed(() => meta.value.letter);
const title = computed(() => meta.value.title);

// Geometry only — the letter scales with the square, so it stays derived.
const dimensions = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  fontSize: `${props.size * 0.5}px`,
}));
</script>

<style scoped>
.source-badge {
  border-radius: var(--p-source-badge-border-radius);
  font-family: var(--p-source-badge-font-family);
  font-weight: var(--p-source-badge-font-weight);
  flex-shrink: 0;
}

.source-badge--floor {
  background: var(--p-source-badge-floor-background);
  color: var(--p-source-badge-floor-color);
}

.source-badge--online {
  background: var(--p-source-badge-online-background);
  color: var(--p-source-badge-online-color);
}

.source-badge--prebid {
  background: var(--p-source-badge-prebid-background);
  color: var(--p-source-badge-prebid-color);
}
</style>
